import React from 'react';
import { Fab, Tooltip, Zoom } from '@mui/material';
import { Lightbulb } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { colors } from '../../theme/theme';
import { ENV_CONFIG } from '../../config/env';
import { isOwnerRole } from '../../utils/roles';

/**
 * Persistent entry point into Feature Requests from anywhere in the panel.
 *
 * Studio OWNERS (isOwnerRole(role)) go to the studio-facing list; SUPER
 * ADMINS go straight to the internal triage page instead — the backend's
 * StudioAdminOnlyGuard rejects every other token (including super_admin) on
 * the studio routes, so showing this to anyone else would only offer a 403.
 * Also only on the Sankalp test instance (ENV_CONFIG.IS_TEST_INSTANCE) —
 * this feature isn't live on the neelniketan production panel yet.
 */
const FeatureRequestsFab: React.FC = () => {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!ENV_CONFIG.IS_TEST_INSTANCE) return null;
  const isStudioOwner = isOwnerRole(admin?.role);
  const isSuperAdmin = admin?.role === 'super_admin';
  if (!isStudioOwner && !isSuperAdmin) return null;

  const destination = isSuperAdmin ? '/admin/feature-requests' : '/feature-requests';
  if (location.pathname.startsWith(destination)) return null;

  return (
    <Zoom in>
      <Tooltip title="Feature Requests" placement="left">
        <Fab
          onClick={() => navigate(destination)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1300,
            backgroundColor: colors.primary,
            color: '#ffffff',
            boxShadow: '0 4px 20px rgba(244, 143, 180, 0.45)',
            '&:hover': {
              backgroundColor: colors.primaryDark,
            },
          }}
        >
          <Lightbulb />
        </Fab>
      </Tooltip>
    </Zoom>
  );
};

export default FeatureRequestsFab;
